import { useCallback } from "react"
import { Route, Routes, useLocation } from "react-router"
import Card from "../components/Card"
import { CourseLinks } from "../components/CourseLinks"
import AssignmentFeedbackView from "../components/feedback/AssignmentFeedbackView"
import Members from "../components/Members"
import Results from "../components/Results"
import StudentDetails from "../components/StudentDetails"
import SubmissionGuide from "../components/student/SubmissionGuide"
import Assignments from "../components/teacher/Assignments"
import CourseLogs from "../components/teacher/CourseLogs"
import EditCourse from "../components/teacher/EditCourse"
import { Color, isManuallyGraded } from "../Helpers"
import { useBackspaceNavigation } from "../hooks/useBackspaceNavigation"
import { useCourseID } from "../hooks/useCourseID"
import { useActions, useAppState } from "../overmind"
import GroupPage from "./GroupPage"

const ReviewResults = () => <Results review />
const RegularResults = () => <Results review={false} />

/* TeacherPage enables routes to be accessed by the teacher only, and displays an overview of the different features available to the teacher. */
const TeacherPage = () => {
    const state = useAppState()
    const actions = useActions().global
    const courseID = useCourseID()
    const location = useLocation()
    const root = `/course/${courseID}`
    const courseHasManualGrading = state.assignments[courseID.toString()]?.some(assignment => isManuallyGraded(assignment.reviewers))

    // Enable Backspace keyboard shortcut to navigate back to root
    useBackspaceNavigation(root)

    const members = {
        title: "View Members",
        notification: state.pendingEnrollments.length > 0 ? { color: Color.YELLOW, text: "Pending enrollments" } : undefined,
        text: "View all students, and approve new enrollments.",
        buttonText: "Members", to: `${root}/members`
    }
    const groups = {
        title: "Manage Groups",
        notification: state.pendingGroups.length > 0 ? { color: Color.YELLOW, text: "Pending groups" } : undefined,
        text: "View, edit or delete course groups.",
        buttonText: "Groups", to: `${root}/groups`
    }
    const results = { title: "View results", text: "View results for all students in the course.", buttonText: "Results", to: `${root}/results` }
    const assignments = { title: "Manage Assignments", text: "View and edit assignments.", buttonText: "Assignments", to: `${root}/assignments` }
    const handleUpdateAssignments = useCallback(() => actions.updateAssignments(courseID), [actions, courseID])
    const updateAssignments = {
        title: "Update Course Assignments",
        text: "Fetch assignments from GitHub.",
        buttonText: "Update Assignments",
        onclick: handleUpdateAssignments
    }
    const editCourse = {
        title: "Edit Course",
        text: "Edit the course name, code, term, year, and slip days.",
        buttonText: "Edit Course",
        to: `${root}/edit`
    }
    const review = { title: "Review Assignments", text: "Review assignments for students.", buttonText: "Review", to: `${root}/review` }
    const feedback = { title: "View Assignment Feedback", text: "View feedback provided by students on assignments.", buttonText: "Feedback", to: `${root}/feedback` }
    const logs = { title: "Course Logs", text: "View webhook, CI, and rebuild activity for this course.", buttonText: "Logs", to: `${root}/logs` }

    return (
        <>
            <CourseLinks />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" hidden={location.pathname !== root}>
                {courseHasManualGrading && <Card {...review} />}
                <Card {...results} />
                <Card {...groups} />
                <Card {...members} />
                <Card {...assignments} />
                <Card {...updateAssignments} />
                <Card {...editCourse} />
                <Card {...feedback} />
                <Card {...logs} />
            </div>
            <Routes>
                <Route path="/groups" element={<GroupPage />} />
                <Route path="/members" element={<Members />} />
                <Route path="/members/:enrollmentID" element={<StudentDetails />} />
                <Route path="/review" element={<ReviewResults />} />
                <Route path="/results" element={<RegularResults />} />
                <Route path="/assignments" element={<Assignments />} />
                <Route path="/feedback" element={<AssignmentFeedbackView />} />
                <Route path="/feedback/:assignmentID" element={<AssignmentFeedbackView />} />
                <Route path="/logs" element={<CourseLogs />} />
                <Route path="/edit" element={<EditCourse />} />
                <Route path="/submission-guide" element={<SubmissionGuide />} />
            </Routes>
        </>
    )
}

export default TeacherPage
